import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import StandardPage from '../../components/StandardPage';
import { useOrganization } from '../../context/organization';
import { toast } from 'react-toastify';
import { handleApiError } from '../../api-client/error_handler';
import { useQueryMetadataContext } from '../../context/query-metadata';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

interface GalleryItem {
  id: string;
  name: string;
  captured_at?: string;
  preview_file_key?: string;
  preview_url: string;
}

interface ContextSatelliteImage {
  task_id: string;
  preview_file_key?: string;
  file_key?: string;
  heading: string;
  description: string;
  title?: string;
  preview_url?: string;
}

interface SdkContextRecord {
  id: string;
  title: string;
  initial_query_state: Record<string, unknown>;
  satellite_images: ContextSatelliteImage[];
  created_at?: string;
  updated_at?: string;
}

interface InitialQueryFormValues {
  category: string;
  locality: string;
  source: string;
  type: string;
  from_date: string;
  to_date: string;
}

interface ContextFormState {
  id?: string;
  title: string;
  initialQueryState: InitialQueryFormValues;
  satelliteImages: ContextSatelliteImage[];
}

const INITIAL_QUERY_FIELDS: (keyof InitialQueryFormValues)[] = ['category', 'locality', 'source', 'type', 'from_date', 'to_date'];

const defaultInitialQueryState = (): InitialQueryFormValues => ({
  category: '',
  locality: '',
  source: '',
  type: '',
  from_date: '',
  to_date: ''
});

const normalizeDateForInput = (value?: unknown): string => {
  if (value == null) return '';
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? '' : value.toISOString().slice(0, 10);
  }
  if (typeof value === 'number') {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10);
  }
  if (typeof value !== 'string') return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const parts = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (parts) {
    const [, month, day, year] = parts;
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? '' : new Date(parsed).toISOString().slice(0, 10);
};

const parseInitialQueryState = (state?: Record<string, unknown>): InitialQueryFormValues => {
  const safeState = state ?? {};
  const getString = (key: string) => {
    const value = safeState[key];
    return typeof value === 'string' ? value : '';
  };
  return {
    category: getString('category'),
    locality: getString('locality'),
    source: getString('source'),
    type: getString('type'),
    from_date: normalizeDateForInput(safeState['from_date']),
    to_date: normalizeDateForInput(safeState['to_date'])
  };
};

const buildInitialQueryPayload = (values: InitialQueryFormValues): Record<string, string> => {
  const payload: Record<string, string> = {};
  INITIAL_QUERY_FIELDS.forEach((field) => {
    const value = values[field];
    if (value) {
      payload[field] = value;
    }
  });
  return payload;
};

const emptyFormState = (): ContextFormState => ({
  title: '',
  initialQueryState: defaultInitialQueryState(),
  satelliteImages: []
});

const SdkContextsPage: React.FC = () => {
  const { organization } = useOrganization();
  const { getAccessTokenSilently } = useAuth0();
  const { data: queryMetadata, loading: metadataLoading, error: metadataError } = useQueryMetadataContext();
  const [contexts, setContexts] = useState<SdkContextRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [formState, setFormState] = useState<ContextFormState>(emptyFormState);
  const [saving, setSaving] = useState<boolean>(false);
  const [copyMessage, setCopyMessage] = useState<string>('');
  const [gallery, setGallery] = useState<GalleryItem[]>([]);
  const [galleryLoading, setGalleryLoading] = useState<boolean>(false);
  const [galleryError, setGalleryError] = useState<string | null>(null);

  const fetchContexts = useCallback(async () => {
    if (!API_BASE_URL || !organization) return;
    try {
      setLoading(true);
      const token = await getAccessTokenSilently();
      const { data } = await axios.get(`${API_BASE_URL}/sdk/contexts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContexts(data.contexts ?? []);
      setError(null);
    } catch (err) {
      toast.error(handleApiError(err));
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently, organization]);

  const fetchGallery = useCallback(async () => {
    if (!API_BASE_URL || !organization) return;
    try {
      setGalleryLoading(true);
      const token = await getAccessTokenSilently();
      const { data } = await axios.get(`${API_BASE_URL}/sdk/gallery`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGallery(data.gallery ?? []);
      setGalleryError(null);
    } catch (err) {
      toast.error(handleApiError(err));
    } finally {
      setGalleryLoading(false);
    }
  }, [getAccessTokenSilently, organization]);

  useEffect(() => {
    fetchContexts();
  }, [fetchContexts]);

  useEffect(() => {
    fetchGallery();
  }, [fetchGallery]);

  const openNewModal = () => {
    setFormState(emptyFormState());
    setIsModalOpen(true);
  };

  const openEditModal = (context: SdkContextRecord) => {
    setFormState({
      id: context.id,
      title: context.title,
      initialQueryState: parseInitialQueryState(context.initial_query_state),
      satelliteImages: (context.satellite_images ?? []).map(image => ({ ...image }))
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSaving(false);
  };

  const handleFormChange = (field: keyof ContextFormState, value: any) => {
    setFormState(prev => ({ ...prev, [field]: value }));
  };

  const upsertContext = async () => {
    if (!API_BASE_URL) return;
    setSaving(true);
    try {
      const token = await getAccessTokenSilently();
      const payload = {
        title: formState.title,
        initial_query_state: buildInitialQueryPayload(formState.initialQueryState),
        satellite_images: formState.satelliteImages.map(image => ({
          task_id: image.task_id,
          preview_file_key: image.preview_file_key,
          file_key: image.file_key,
          heading: image.heading,
          description: image.description,
          title: image.title
        }))
      };
      const url = formState.id ? `${API_BASE_URL}/sdk/contexts/${formState.id}` : `${API_BASE_URL}/sdk/contexts`;
      const method = formState.id ? 'PUT' : 'POST';
      const config = {
        method,
        url,
        headers: {
          Authorization: `Bearer ${token}`
        },
        data: payload
      } as const;
      await axios(config);
      await fetchContexts();
      closeModal();
    } catch (err) {
      toast.error(handleApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (context: SdkContextRecord) => {
    if (!API_BASE_URL || !window.confirm(`Delete context "${context.title}"?`)) return;
    try {
      const token = await getAccessTokenSilently();
      await axios.delete(`${API_BASE_URL}/sdk/contexts/${context.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchContexts();
    } catch (err) {
      toast.error(handleApiError(err));
    }
  };

  const handleCopyCode = async (contextId: string) => {
    const snippet = `<div class="civapi-experience" data-civapi-context-id="${contextId}"></div>`;
    try {
      await navigator.clipboard.writeText(snippet);
      setCopyMessage('Copied experience snippet!');
      setTimeout(() => setCopyMessage(''), 2000);
    } catch (err) {
      console.error('[SDK Contexts] copy snippet error', err);
      setCopyMessage('Unable to copy snippet.');
    }
  };

  const toggleGallerySelection = (item: GalleryItem) => {
    setFormState(prev => {
      const exists = prev.satelliteImages.find(image => image.task_id === item.id);
      if (exists) {
        return {
          ...prev,
          satelliteImages: prev.satelliteImages.filter(image => image.task_id !== item.id)
        };
      }
      if (!item.preview_file_key) {
        setError('Selected satellite image is missing a preview. Please contact CivAPI support.');
        return prev;
      }
      return {
        ...prev,
        satelliteImages: [
          ...prev.satelliteImages,
          {
            task_id: item.id,
            preview_file_key: item.preview_file_key,
            heading: item.name,
            title: item.name,
            description: '',
            preview_url: item.preview_url
          }
        ]
      };
    });
  };

  const updateSatelliteImage = (index: number, field: keyof ContextSatelliteImage, value: string) => {
    const updated = [...formState.satelliteImages];
    updated[index] = { ...updated[index], [field]: value };
    handleFormChange('satelliteImages', updated);
  };

  const removeSatelliteImage = (index: number) => {
    const updated = formState.satelliteImages.filter((_, idx) => idx !== index);
    handleFormChange('satelliteImages', updated);
  };

  const modalTitle = formState.id ? 'Edit context' : 'Create context';

  const selectedTaskIds = useMemo(() => new Set(formState.satelliteImages.map(image => image.task_id)), [formState.satelliteImages]);
  const categoryOptions = queryMetadata.filters ?? [];
  const localityOptions = queryMetadata.localities ?? [];
  const selectedCategoryOption = categoryOptions.find(opt => opt.value === formState.initialQueryState.category);
  const sourceOptions = selectedCategoryOption?.options ?? [];
  const selectedSourceOption = sourceOptions.find(opt => opt.value === formState.initialQueryState.source);
  const typeOptions = selectedSourceOption?.options ?? [];
  const selectedTypeGroup = (() => {
    if (!formState.initialQueryState.type) return null;
    const direct = typeOptions.find(opt => opt.value === formState.initialQueryState.type);
    if (direct) return direct;
    return typeOptions.find(opt => (opt.options ?? []).some(child => child.value === formState.initialQueryState.type)) ?? null;
  })();
  const typeSelectValue = selectedTypeGroup
    ? (selectedTypeGroup.options && selectedTypeGroup.options.length > 0 ? selectedTypeGroup.value : formState.initialQueryState.type)
    : '';
  const subtypeOptions = selectedTypeGroup?.options ?? [];
  const showTypeField = typeOptions.length > 0;
  const showSubtypeField = subtypeOptions.length > 0 && !!typeSelectValue;
  const typeFieldLabel = typeOptions[0]?.dropdown_label ?? 'Type';
  const subtypeFieldLabel = subtypeOptions[0]?.dropdown_label ?? 'Sub-Type';

  useEffect(() => {
    setFormState(prev => {
      const next = { ...prev.initialQueryState };
      let updated = false;

      if (next.category) {
        const category = categoryOptions.find(opt => opt.value === next.category);
        if (!category) {
          next.category = '';
          next.source = '';
          next.type = '';
          updated = true;
        } else {
          if (next.source) {
            const source = (category.options ?? []).find(opt => opt.value === next.source);
            if (!source) {
              next.source = '';
              next.type = '';
              updated = true;
            } else if (next.type) {
              const validType = (source.options ?? []).some(option =>
                option.value === next.type || (option.options ?? []).some(child => child.value === next.type)
              );
              if (!validType) {
                next.type = '';
                updated = true;
              }
            }
          } else if (next.type) {
            next.type = '';
            updated = true;
          }
        }
      }

      if (next.locality) {
        const exists = localityOptions.some(opt => opt.value === next.locality);
        if (!exists) {
          next.locality = '';
          updated = true;
        }
      }

      return updated ? { ...prev, initialQueryState: next } : prev;
    });
  }, [categoryOptions, localityOptions]);

  useEffect(() => {
    if (metadataError) {
      toast.error(metadataError);
    }
  }, [metadataError]);

  const updateQueryStateField = (field: keyof InitialQueryFormValues, value: string) => {
    setFormState(prev => ({
      ...prev,
      initialQueryState: {
        ...prev.initialQueryState,
        [field]: value
      }
    }));
  };

  const handleCategoryChange = (value: string) => {
    setFormState(prev => {
      const category = categoryOptions.find(opt => opt.value === value);
      const availableSources = category?.options ?? [];
      const sourceValid = availableSources.some(opt => opt.value === prev.initialQueryState.source);
      const nextSource = sourceValid ? prev.initialQueryState.source : '';
      return {
        ...prev,
        initialQueryState: {
          ...prev.initialQueryState,
          category: value,
          source: nextSource,
          type: ''
        }
      };
    });
  };

  const handleSourceChange = (value: string) => {
    setFormState(prev => ({
      ...prev,
      initialQueryState: {
        ...prev.initialQueryState,
        source: value,
        type: ''
      }
    }));
  };

  const handleTypeChange = (value: string) => {
    if (!value) {
      updateQueryStateField('type', '');
      return;
    }
    const match = typeOptions.find(opt => opt.value === value);
    if (!match) {
      updateQueryStateField('type', '');
      return;
    }
    if (match.options && match.options.length > 0) {
      updateQueryStateField('type', match.options[0].value);
    } else {
      updateQueryStateField('type', match.value);
    }
  };

  const handleSubtypeChange = (value: string) => {
    updateQueryStateField('type', value);
  };

  if (!organization) {
    return <StandardPage title="CivAPI Contexts"><p>Loading organization data...</p></StandardPage>;
  }

  if (!organization.has_sdk_license) {
    return (
      <StandardPage title="CivAPI Contexts">
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-amber-900">
          <h2 className="text-xl font-semibold mb-2">SDK license required</h2>
          <p>Contact CivAPI to enable SDK access for your organization before managing contexts.</p>
        </div>
      </StandardPage>
    );
  }

  return (
    <StandardPage title="CivAPI Contexts">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-slate-900">Manage your contextual experiences</h2>
          <p className="text-sm text-slate-500">Create contexts that embed query defaults and satellite imagery.</p>
        </div>
        <button
          onClick={openNewModal}
          className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500"
        >
          New context
        </button>
      </div>

      {error && <p className="mb-4 text-sm text-rose-600">{error}</p>}

      {loading ? (
        <p className="text-slate-500">Loading contexts...</p>
      ) : (
        <div className="overflow-x-auto rounded-3xl border border-slate-100 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-slate-100">
            <thead className="bg-slate-50/50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Title</th>
                <th className="px-4 py-3">Created</th>
                <th className="px-4 py-3">Satellite images</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-sm text-slate-700">
              {contexts.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-6 text-center text-slate-500">
                    No contexts yet. Create one to get started.
                  </td>
                </tr>
              )}
              {contexts.map(context => (
                <tr key={context.id}>
                  <td className="px-4 py-3 font-semibold">{context.title}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">{context.created_at ? new Date(context.created_at).toLocaleString() : '—'}</td>
                  <td className="px-4 py-3">{context.satellite_images?.length ?? 0}</td>
                  <td className="px-4 py-3 text-right space-x-2">
                    <button
                      onClick={() => handleCopyCode(context.id)}
                      className="rounded-xl border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"
                    >
                      Copy code
                    </button>
                    <button
                      onClick={() => openEditModal(context)}
                      className="rounded-xl border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(context)}
                      className="rounded-xl border border-rose-200 px-3 py-1.5 text-xs font-semibold text-rose-600 hover:bg-rose-50"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {copyMessage && <p className="mt-2 text-sm text-slate-500">{copyMessage}</p>}

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
          <div className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-3xl bg-white p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900">{modalTitle}</h3>
              <button onClick={closeModal} className="text-slate-500 hover:text-slate-800">
                ✕
              </button>
            </div>
            <div className="space-y-5">
              <div>
                <label className="text-sm font-medium text-slate-700">Title</label>
                <input
                  type="text"
                  className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                  value={formState.title}
                  onChange={(e) => handleFormChange('title', e.target.value)}
                />
              </div>
              <div className="rounded-2xl border border-slate-100 p-4">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-slate-700">Initial query defaults</label>
                  {metadataLoading && <span className="text-xs text-slate-400 animate-pulse">Loading options…</span>}
                </div>
                <p className="mt-1 text-xs text-slate-500">Pick the default filters users will see when launching this context.</p>
                <div className="mt-4 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  <div>
                    <label className="text-xs font-semibold text-slate-600">Category</label>
                    <select
                      className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                      value={formState.initialQueryState.category}
                      disabled={categoryOptions.length === 0}
                      onChange={(e) => handleCategoryChange(e.target.value)}
                    >
                      <option value="">Select category</option>
                      {categoryOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-600">Source</label>
                    <select
                      className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                      value={formState.initialQueryState.source}
                      disabled={sourceOptions.length === 0}
                      onChange={(e) => handleSourceChange(e.target.value)}
                    >
                      <option value="">Select source</option>
                      {sourceOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  {showTypeField && (
                    <div>
                      <label className="text-xs font-semibold text-slate-600">{typeFieldLabel}</label>
                      <select
                        className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                        value={typeSelectValue}
                        onChange={(e) => handleTypeChange(e.target.value)}
                      >
                        <option value="">{`Select ${typeFieldLabel.toLowerCase()}`}</option>
                        {typeOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  {showSubtypeField && (
                    <div>
                      <label className="text-xs font-semibold text-slate-600">{subtypeFieldLabel}</label>
                      <select
                        className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                        value={formState.initialQueryState.type}
                        onChange={(e) => handleSubtypeChange(e.target.value)}
                      >
                        {subtypeOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div>
                    <label className="text-xs font-semibold text-slate-600">Locality</label>
                    <select
                      className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                      value={formState.initialQueryState.locality}
                      disabled={localityOptions.length === 0}
                      onChange={(e) => updateQueryStateField('locality', e.target.value)}
                    >
                      <option value="">Select locality</option>
                      {localityOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-600">From date</label>
                    <input
                      type="date"
                      className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                      value={formState.initialQueryState.from_date}
                      onChange={(e) => updateQueryStateField('from_date', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-600">To date</label>
                    <input
                      type="date"
                      className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                      value={formState.initialQueryState.to_date}
                      onChange={(e) => updateQueryStateField('to_date', e.target.value)}
                    />
                  </div>
                </div>
                {metadataError && (
                  <p className="mt-2 text-xs text-rose-600">Unable to load filter options. Save is still available for the rest of the form.</p>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-slate-700">Satellite gallery</label>
                  {galleryError && <span className="text-xs text-rose-600">{galleryError}</span>}
                </div>
                {galleryLoading ? (
                  <p className="mt-2 text-sm text-slate-500">Loading gallery...</p>
                ) : (
                  <div className="mt-3 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {gallery.length === 0 && (
                      <p className="text-sm text-slate-500">No satellite imagery available yet. Order images to populate this gallery.</p>
                    )}
                    {gallery.map(item => {
                      const selected = selectedTaskIds.has(item.id);
                      return (
                        <button
                          type="button"
                          key={item.id}
                          onClick={() => toggleGallerySelection(item)}
                          className={`relative overflow-hidden rounded-2xl border text-left transition hover:shadow ${selected ? 'border-blue-500 ring-2 ring-blue-200' : 'border-slate-200'}`}
                        >
                          <img src={item.preview_url} alt={item.name} className="h-36 w-full object-cover" />
                          <div className="p-3">
                            <p className="text-sm font-semibold text-slate-900">{item.name}</p>
                            <p className="text-xs text-slate-500">{item.captured_at ? new Date(item.captured_at).toLocaleDateString() : 'Delivery ready'}</p>
                          </div>
                          <span className={`absolute top-3 right-3 rounded-full px-3 py-1 text-xs font-semibold ${selected ? 'bg-blue-600 text-white' : 'bg-white/80 text-slate-700'}`}>
                            {selected ? 'Selected' : 'Add'}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-slate-700">Selected images</label>
                  <p className="text-xs text-slate-500">Customize captions and descriptions for each image.</p>
                </div>
                {formState.satelliteImages.length === 0 && (
                  <p className="mt-2 text-sm text-slate-500">Choose at least one image from the gallery above.</p>
                )}
                <div className="mt-3 space-y-3">
                  {formState.satelliteImages.map((image, index) => (
                    <div key={`${image.task_id}-${index}`} className="rounded-2xl border border-slate-100 p-4 shadow-sm">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          {image.preview_url && (
                            <img src={image.preview_url} alt={image.title || image.heading} className="h-14 w-20 rounded-lg object-cover" />
                          )}
                          <div>
                            <p className="text-sm font-semibold text-slate-900">{image.title || 'Selected image'}</p>
                            <p className="text-xs text-slate-500">Task ID: {image.task_id}</p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeSatelliteImage(index)}
                          className="text-xs font-semibold text-rose-600 hover:text-rose-500"
                        >
                          Remove
                        </button>
                      </div>
                      <div className="grid gap-3 md:grid-cols-2">
                        <div>
                          <label className="text-xs font-medium text-slate-600">Heading</label>
                          <input
                            type="text"
                            className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                            value={image.heading}
                            onChange={(e) => updateSatelliteImage(index, 'heading', e.target.value)}
                          />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-slate-600">Title (optional)</label>
                          <input
                            type="text"
                            className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                            value={image.title ?? ''}
                            onChange={(e) => updateSatelliteImage(index, 'title', e.target.value)}
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="text-xs font-medium text-slate-600">Description</label>
                          <textarea
                            className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                            rows={2}
                            value={image.description}
                            onChange={(e) => updateSatelliteImage(index, 'description', e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={closeModal}
                className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={upsertContext}
                className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 disabled:opacity-60"
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save context'}
              </button>
            </div>
          </div>
        </div>
      )}
    </StandardPage>
  );
};

export default SdkContextsPage;
